// Who is signed in, as the header shows it: the display name, with the email and role underneath.
// Never the subject: a SAML subject is a pseudonym (`saml-` and a hash) that means nothing to a
// person. `name` / `email` come from GET /v1/me when the BFF knows them.
import type { Me } from "../session/session";

function clean(value: string | null | undefined): string | null {
  const text = value?.trim();
  return text ? text : null;
}

/** The role in plain words: "Super admin", "Billing admin", "Installer", … */
export function roleLabel(me: Pick<Me, "role" | "permissions">): string {
  if (me.role === "super-admin") return "Super admin";
  if (me.role === "org-admin") return "Customer admin";
  const has = (permission: Me["permissions"][number]) => me.permissions.includes(permission);
  const parts: string[] = [];
  if (has("billing:write") || has("billing:approve")) parts.push("Billing admin");
  else if (has("billing:read")) parts.push("Billing viewer");
  if (has("installer:run") || has("installer:approve")) parts.push("Installer");
  return parts.length > 0 ? parts.join(" + ") : "No role";
}

export interface UserLines {
  /** The first line: the person's name, else their email, else "Signed in". */
  name: string;
  /** The second line: the email (when the name is not already it) and the role. */
  meta: string;
}

export function userLines(me: Me): UserLines {
  const name = clean(me.name);
  const email = clean(me.email);
  const first = name ?? email ?? "Signed in";
  const meta = [email !== null && email !== first ? email : null, roleLabel(me)].filter((part): part is string => part !== null);
  return { name: first, meta: meta.join(" · ") };
}

/** A pseudonymous SAML subject (`saml-` and 32 hex digits): it means nothing to a person. */
export function isPseudonymousSubject(id: string): boolean {
  return /^saml-[0-9a-f]{8,}$/i.test(id.trim());
}

/** What the API knows about an admin behind an actor field: its `<field>Name` / `<field>Email`. */
export interface KnownActor {
  name?: string | null | undefined;
  email?: string | null | undefined;
}

/** The `<field>Name` / `<field>Email` siblings of an actor field (`closedBy` → `closedByName`, …). */
export function knownActor(record: object | null | undefined, field: string): KnownActor {
  const values = (record ?? {}) as Record<string, unknown>;
  const text = (value: unknown): string | null => (typeof value === "string" ? value : null);
  return { name: text(values[`${field}Name`]), email: text(values[`${field}Email`]) };
}

/**
 * Who did something ("Closed by", "Created by", "Finalized by", "Approved by"), from the admin
 * subject the API recorded and the name / email it resolved for it: the signed-in person is "You",
 * anyone else their name, else their email, else "An admin". Never the raw subject — a SAML subject
 * is a pseudonym and any subject is an internal id.
 */
export function actorDisplay(id: string | null | undefined, me?: Pick<Me, "subject"> | null, known?: KnownActor | null): string {
  const text = id?.trim() ?? "";
  if (text === "") return "—";
  if (me && text === me.subject) return "You";
  return clean(known?.name) ?? clean(known?.email) ?? "An admin";
}

/** The tooltip for an actor: its email when the API knows it (the subject never). */
export function actorTitle(_id: string | null | undefined, known?: KnownActor | null): string | undefined {
  return clean(known?.email) ?? undefined;
}
